import type { DocsFormDataMapped, DocsFormName } from "core/types";
import { format } from "date-fns";
import { renderToStaticMarkup } from "react-dom/server";
import Translations from "translations/types";
import { PdfCreatorProps } from "..";

export type FieldConfig<T extends DocsFormName> = {
  label: (translations: Translations) => string;
  key: keyof DocsFormDataMapped<T>;
};

// html2pdf does not support some CSS functions commonly used in Tailwind, so inline styles must be used.
type Section = { label: string; number: number; value: string };

const pdfStyles = {
  footer: {
    borderTop: "1px solid #e5e5e5",
    color: "#737373",
    fontSize: "9pt",
    marginTop: "32px",
    paddingTop: "12px",
  },
  header: {
    alignItems: "flex-start",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "24px",
  },
  headerTitle: { fontSize: "24px", fontWeight: 700, margin: 0 },
  root: {
    color: "#000000",
    fontFamily: "sans-serif",
    lineHeight: 1.375,
    maxWidth: "210mm",
    padding: "24px",
  },
  section: { marginBottom: "16px" },
  sectionLabel: { fontSize: "18px", fontWeight: 700, marginBottom: "4px" },
  sectionValue: { whiteSpace: "pre-wrap", wordBreak: "break-word" },
} as const;

function DischargeLetterSection({ label, number, value }: Section) {
  return (
    <div style={pdfStyles.section}>
      <div style={pdfStyles.sectionLabel}>
        {number}. {label}
      </div>
      <div style={pdfStyles.sectionValue}>{value}</div>
    </div>
  );
}

function DischargeLetterDocument<T extends DocsFormName>({
  createdBy,
  fieldConfig,
  formData,
  locale,
  translations,
}: PdfCreatorProps<T> & { fieldConfig: Array<FieldConfig<T>> }) {
  const title = translations.extractDocs.dischargeLetter.generatedPdf.title;
  const createdByText =
    translations.extractDocs.dischargeLetter.generatedPdf.createdBy({
      date: format(new Date(), "dd.MM.yyyy, HH:mm", { locale }),
      name: createdBy ?? "Recare",
    });

  const sections: Section[] = fieldConfig
    .filter((field) => {
      const value = formData[field.key];
      return value != null && String(value).trim() !== "";
    })
    .map((field, index) => ({
      label: field.label(translations),
      number: index + 1,
      value: String(formData[field.key] ?? "").trim(),
    }));

  return (
    <div style={pdfStyles.root}>
      <header style={pdfStyles.header}>
        <div style={{ flex: 1 }}>
          <h1 style={pdfStyles.headerTitle}>{title}</h1>
        </div>
      </header>
      <main>
        {sections.map((section) => (
          <DischargeLetterSection
            key={section.number}
            label={section.label}
            number={section.number}
            value={section.value}
          />
        ))}
      </main>
      <footer style={pdfStyles.footer}>{createdByText}</footer>
    </div>
  );
}

export async function createDischargeLetterPdf<T extends DocsFormName>(
  props: PdfCreatorProps<T>,
  fieldConfig: Array<FieldConfig<T>>,
): Promise<Uint8Array> {
  const html = renderToStaticMarkup(
    <DischargeLetterDocument {...props} fieldConfig={fieldConfig} />,
  );
  const container = document.createElement("div");
  container.innerHTML = html;

  const html2pdf = (await import("html2pdf.js")).default;
  const arrayBuffer = await html2pdf()
    .set({
      html2canvas: {
        foreignObjectRendering: false,
        removeContainer: true,
        scale: 1.0,
      },
      image: { quality: 0.75, type: "jpeg" },
      jsPDF: { format: "a4", unit: "mm" },
    })
    .from(container)
    .outputPdf("arraybuffer");

  return new Uint8Array(arrayBuffer as ArrayBuffer);
}
